import {
  type ProjectCreate,
  type ProjectUpdate,
  pageSchema,
  projectSchema,
  reportCountsSchema,
  type WorkspaceUpdate,
  workspaceSchema,
} from "@repro/shared";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "../../lib/api-client";

export const workspaceKeys = {
  all: (workspaceId: string) => ["workspace", workspaceId] as const,
  detail: (workspaceId: string) => ["workspace", workspaceId, "detail"] as const,
  projects: (workspaceId: string) => ["workspace", workspaceId, "projects"] as const,
  counts: (workspaceId: string, projectId?: string) =>
    ["workspace", workspaceId, "counts", projectId ?? "all"] as const,
};

export const workspaceQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: workspaceKeys.detail(workspaceId),
    queryFn: () => api(`/workspaces/${workspaceId}`, { schema: workspaceSchema }),
  });

export const projectsQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: workspaceKeys.projects(workspaceId),
    queryFn: async () =>
      (
        await api(`/workspaces/${workspaceId}/projects`, {
          schema: z.object({ items: z.array(projectSchema) }),
        })
      ).items,
  });

export const countsQuery = (workspaceId: string, projectId?: string) =>
  queryOptions({
    queryKey: workspaceKeys.counts(workspaceId, projectId),
    queryFn: () =>
      api(`/workspaces/${workspaceId}/reports/summary`, {
        query: { projectId },
        schema: reportCountsSchema,
      }),
  });

export { pageSchema };

/** Admin: name, retention, logs URL, public links and privacy; only the fields sent change. */
export function updateWorkspace(workspaceId: string, input: WorkspaceUpdate) {
  return api(`/workspaces/${workspaceId}`, {
    method: "PATCH",
    body: input,
    schema: workspaceSchema,
  });
}

export function createProject(workspaceId: string, input: ProjectCreate) {
  return api(`/workspaces/${workspaceId}/projects`, {
    method: "POST",
    body: input,
    schema: projectSchema,
  });
}

export function updateProject(workspaceId: string, projectId: string, input: ProjectUpdate) {
  return api(`/workspaces/${workspaceId}/projects/${projectId}`, {
    method: "PATCH",
    body: input,
    schema: projectSchema,
  });
}

/** Owner only: removes everything in the workspace, stored files included. */
export function deleteWorkspace(workspaceId: string) {
  return api(`/workspaces/${workspaceId}`, { method: "DELETE" });
}
